/**
 * Provides an administration interface for configuring application session
 * management settings, including inactivity timeouts and warning thresholds.
 * Allows administrators to define automatic logout behavior, validate session
 * timing rules, track unsaved changes, and persist session-related
 * configuration updates across the application.
 */
import React, { useEffect, useState } from "react";

import { getCommonConfig, updateCommonConfig } from "@/services/commonconfig";

const SessionConfig: React.FC = () => {
  const [logout, setLogout] = useState("");
  const [warning, setWarning] = useState("");
  const [inactive, setInactive] = useState(0);

  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const res = await getCommonConfig();

    setLogout(String(res.LogoutTime));
    setWarning(String(res.WarningTime));
    setInactive(res.InactiveDays);

    setInitial(
      JSON.stringify({
        LogoutTime: res.LogoutTime,
        WarningTime: res.WarningTime,
        InactiveDays: res.InactiveDays,
      })
    );
  };

  const hasChanges =
    initial !== "" &&
    JSON.stringify({
      LogoutTime: Number(logout || 0),
      WarningTime: Number(warning || 0),
      InactiveDays: inactive,
    }) !== initial;

  const isValid = Number(logout || 0) > Number(warning || 0);

  const handleSave = async () => {
    if (!isValid) return;

    setSaving(true);

    await updateCommonConfig({
      LogoutTime: Number(logout),
      WarningTime: Number(warning),
      InactiveDays: inactive,
    });

    setInitial(
      JSON.stringify({
        LogoutTime: Number(logout),
        WarningTime: Number(warning),
        InactiveDays: inactive,
      })
    );

    setSaving(false);
  };

  const handleCancel = () => {
    const parsed = JSON.parse(initial);

    setLogout(String(parsed.LogoutTime));
    setWarning(String(parsed.WarningTime));
    setInactive(parsed.InactiveDays);
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      {!isValid && logout !== "" && warning !== "" && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          Logout Time should be greater than Warning Time.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col text-xs">
          <label className="mb-1 text-gray-600">Logout Time (mins)</label>
          <input
            type="text"
            inputMode="numeric"
            className="border border-gray-300 px-2 py-1 rounded focus:ring-2 focus:ring-orange-400"
            value={logout}
            onChange={(e) => setLogout(e.target.value.replace(/[^0-9]/g, ""))}
          />
        </div>

        <div className="flex flex-col text-xs">
          <label className="mb-1 text-gray-600">Warning Time (mins)</label>
          <input
            type="text"
            inputMode="numeric"
            className="border border-gray-300 px-2 py-1 rounded focus:ring-2 focus:ring-orange-400"
            value={warning}
            onChange={(e) => setWarning(e.target.value.replace(/[^0-9]/g, ""))}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 items-center">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 rounded text-xs disabled:opacity-50"
          onClick={handleSave}
          disabled={!hasChanges || saving || !isValid}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default SessionConfig;
